// @ts-check
import React from 'react';
import { FaArrowUp, FaArrowDown, FaMinus } from 'react-icons/fa';

import { parseISO, isAfter, subDays } from 'date-fns';
import { COLOURS } from '@home/config';
import { getEventVisual } from "@components/cards/events/events";
import { getNestedValue } from '@datahooks/metadata/DataUtils';

import PropTypes from 'prop-types';

export const SPARK_TREND_DAYS = 7;  // Used to reduce trend data used in sparklines on overview cards

// Utility to calculate the Exponential Moving Average (EMA)
export const calculateEMA = (data, key, period = 10) => {
    const ema = [];
    let previousEma;
    const alpha = 2 / (period + 1);  // Smoothing factor based on the period

    data.forEach((item, index) => {
      if (index === 0) {
        previousEma = item[key];  // The first EMA is just the first data point
      } else {
        // EMA calculation using the standard formula
        previousEma = (item[key] - previousEma) * alpha + previousEma;
      }
       // Round the trend value to two decimal places
        ema.push({ ...item, trend: Math.round(previousEma * 100) / 100 });
    });

    return ema;
};

// Function to format the tooltip based on metric type
export const formatTooltipValue = (value, selectedMetric, name) => {
    
  if (selectedMetric === 'technical_debt') {
    return `${Number(value).toLocaleString()} hours`; // Format as hours for technical debt
  }

  if ((selectedMetric === 'response_time') || (selectedMetric === '95th_pc_response_time')) {
    return `${value.toFixed(2)}ms`; // Format as milliseconds for response time
  }

  if (selectedMetric === 'throughput') {
    return `${Number(value.toFixed(2)).toLocaleString()}tps`;
  }

  if (selectedMetric === 'pass_fail') {
    return `${Number(value.toFixed(2)).toLocaleString()}%`; // Format as percentage for pass/fail
  }

  if (name === 'Trend Line') {
    return `${Number(value.toFixed(2)).toLocaleString()}`; // Format as two decimal places for trend line
  }
  
  return Number(value).toLocaleString(); // Default format for other metrics
};

export function getGradient(selectedMetric, barDataKey) {
  // For percentage metrics, base the gradient on the barDataKey (pass or fail)
  if (['pass_fail', 'pass_fail_count'].includes(selectedMetric)) {
    switch (barDataKey) {
      case 'passed':
        return 'url(#passGradient)';
      case 'failed':
        return 'url(#failGradient)';
      case 'errors':
        return 'url(#errorsGradient)';
      default:
        return 'url(#passGradient)';
    }
  }

  //For non percentage metrics (SCA metrics, which are negatives), base the gradient on the fail colour
  return 'url(#failGradient)';
}

export const SCA_METRICS = [
  { metric: 'bugs', goodDir: 'down', label: 'Bugs' },
  { metric: 'code_smells', goodDir: 'down', label: 'Code Smells' },
  { metric: 'vulnerabilities', goodDir: 'down', label: 'Vulnerabilities' },
  { metric: 'technical_debt', goodDir: 'down', label: 'Technical Debt' },
  { metric: 'duplicated_blocks', goodDir: 'down', label: 'Duplicated Blocks' },
  { metric: 'cognitive_complexity', goodDir: 'down', label: 'Cognitive Complexity' },
];

export const FUNCTIONAL_UI_METRICS = [
  { metric: 'pass_fail', goodDir: 'up', label: 'Pass Rate' },
  { metric: 'pass_fail_count', goodDir: 'up', label: 'Tests Passed' }
];

export const API_METRICS = [
  { metric: 'pass_fail', goodDir: 'up', label: 'Pass Rate' },
  { metric: 'pass_fail_count', goodDir: 'up', label: 'Tests Passed' }
];

/**
 * Determines the trend color based on the good direction and the actual trend value.
 * @param {number} trendValue - The actual trend value (e.g., 1, -1, or 0).
 * @param {string} [goodDirection='up'] - The direction that is considered positive ('up' by default).
 * @returns {string} - The color suffix (e.g., 'green-500', 'pink-500', 'orange-500') for use in classes.
 */
export const determineTrendColor = (trendValue, goodDirection = 'up') => {
  const isGoodDirectionUp = goodDirection === 'up';

  if (trendValue > 0) {
      // Positive trend: if good direction is up, use green; otherwise, pink
      return isGoodDirectionUp ? 'green' : 'pink';
  } else if (trendValue < 0) {
      // Negative trend: if good direction is not up, use green; otherwise, pink
      return isGoodDirectionUp ? 'pink' : 'green';
  } else {
      // No trend change
      return 'orange';
  }
};

export const TrendIcon = ({ overallTrend = 0, positiveDirection = 'up', testTypeId, iconSize=40}) => {
  const adjustedDirection = [1, 4].includes(testTypeId) ? positiveDirection : 'up';  // Default to 'up' for pass/fail metrics
  const trendDirection = Math.sign(overallTrend);  // Convert value into 1, 0, or -1
  const trendColour = determineTrendColor(trendDirection, adjustedDirection);

  switch(trendDirection) {
    case 1:
      return <FaArrowUp data-testid="trendIcon" size={iconSize} className={`text-${trendColour}-500`} />;
    case -1:
      return <FaArrowDown data-testid="trendIcon" size={iconSize} className={`text-${trendColour}-500`}  />;
    case 0:
    default:
      return <FaMinus data-testid="trendIcon" size={iconSize} className="text-orange-500" />;
  }
};

TrendIcon.propTypes = {
  overallTrend: PropTypes.number,
  positiveDirection: PropTypes.string,
  testTypeId: PropTypes.number,
  iconSize: PropTypes.number
};

/**
 * Normalise sparkline data points for overview cards.
 *
 * @param {Array<any>} trendData - The raw trend data array
 * @param {number} [daysLimit=SPARK_TREND_DAYS] - The number of days to include in the sparkline.
 * @param {((item: any) => number) | null} [valueExtractor=null] - Optional custom function to extract the value for the sparkline from each data item.
 *
 * @returns {Array<object>} - The formatted sparkline data array.
 */
export const formatSparkData = (trendData, daysLimit = SPARK_TREND_DAYS, valueExtractor = null) => {
  if (!Array.isArray(trendData)) {
      return [];
  }

  /** @type {((item: any) => number) | null} */
  const extractor = valueExtractor;

  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysLimit);

  const filteredByDate = trendData
    .filter((item) => { // Remove any items that are outside of the desired date range or do not have a valid release date
      const releaseDate = item.release_date || item.releaseDate;
      if (!releaseDate) {
        return false;
      }

      const itemDate = new Date(releaseDate);
      return itemDate >= cutoffDate;
    })
    .sort((itemA, itemB) => { // Sort by release date to handle instances where data is not returned in chronological order
      const dateA = new Date(itemA.release_date || itemA.releaseDate).getTime();  // Use getTime() for numerical comparison
      const dateB = new Date(itemB.release_date || itemB.releaseDate).getTime();
      return dateA - dateB;
    });

  return filteredByDate.map((item) => {
    const release = item.release_name || item.version || item.name || item.release;
    let value;

    if (extractor) {
      // Use the provided callback to get the value
      value = extractor(item);
    } else {
      // Default pass rate calculation for pass/fail data
      const passed = item.passed === undefined ? item.Passed : item.passed;
      const failed = item.failed === undefined ? item.Failed : item.failed;
      const totalTests = passed + failed;
      value = totalTests > 0 ? (passed / totalTests) * 100 : 0; // Avoid division by zero
    }

    if (!Number.isFinite(value)) {    // Disregard entries with non-finite values (e.g., null, undefined, NaN, or Infinity) to prevent charting issues
      return null;
    }

    return {
      version_id: release,
      value: Math.round(value)
    };
  })
    .filter(Boolean)    // Remove any null entries resulting from non-finite values and add an index for ordering in the sparkline
    .map((item, index) => ({
      ...item,
      index,
    }));
};

/**
 * Return a regular colour from the bar gradients
 * 
 * @param {string} aGradient - The gradient string (e.g., 'url(#passGradient)')
 * @returns {string} - The extracted colour (e.g., '#00FF00')
 */
export const getColourFromGradient = (aGradient) => {
  if (!aGradient) return '#000'

  let barColour;

  switch (aGradient) {
    case 'url(#passGradient)' : barColour = COLOURS.pass; break;
    case 'url(#failGradient)' : barColour = COLOURS.fail; break;
    case 'url(#errorsGradient)' : barColour = COLOURS.errors; break;
    default: return '#000';
  }

  return barColour;
};

TrendIcon.propTypes = {
  overallTrend: PropTypes.number.isRequired,
  positiveDirection: PropTypes.string.isRequired,
  testTypeId: PropTypes.number.isRequired,
  iconSize: PropTypes.number
};

formatSparkData.propTypes = {
  trendData: PropTypes.array.isRequired
};

// Custom getAllEventMarkers that handles all event types (not just type 11)
export const getAllEventMarkers = (releases, events, daysFilter) => {
  if (!releases.length || !events.length) return [];
  
  const currentDate = new Date();
  const releasesWithParsedDate = releases.map(release => ({
    ...release,
    parsedDate: parseISO(release.release_date)
  }));
  
  const relevantEvents = events.filter(event => {
    const eventTime = parseISO(event.start_time);
    return isAfter(eventTime, subDays(currentDate, daysFilter));
  });
  
  const findLatestReleaseBefore = (releases, eventTime) => {
    let latestRelease = null;
    for (const release of releases) {
      if (release.parsedDate <= eventTime) {
        latestRelease = release;
      } else {
        break;
      }
    }
    return latestRelease;
  };
  
  const markers = relevantEvents.map(event => {
    const eventTime = parseISO(event.start_time);
    const release = findLatestReleaseBefore(releasesWithParsedDate, eventTime);
    return release ? { event, release } : null;
  }).filter(Boolean);

  return markers;
};

/**
 * Calculate an appropriate icon size based on the average spacing of x-axis points.
 * 
 * @param {function(any):number & {domain:()=>any[], range:()=>number[]}} xScale - The x-axis scale function from Recharts
 * @param {number} minSize - Minimum icon size in pixels
 * @param {number} maxSize - Maximum icon size in pixels
 * @returns {number} - The calculated icon size, clamped between minSize and maxSize
 */
export function getIconSizeFromXScale(xScale, minSize = 20, maxSize = 32) {
  // @ts-ignore - domain and range are assigned dynamically by Recharts
  if (!xScale?.domain || !xScale?.range) return minSize;

  // @ts-ignore - domain and range are assigned dynamically by Recharts
  const domain = xScale.domain();
  if (!domain || domain.length < 2) return minSize;

  const positions = domain.map(d => xScale(d)).filter(v => typeof v === 'number' && !Number.isNaN(v));

  if (positions.length < 2) return minSize;

  let totalSpacing = 0;
  for (let i = 1; i < positions.length; i++) {
    totalSpacing += (positions[i] - positions[i - 1]);
  }

  const avgSpacing = totalSpacing / (positions.length - 1);

  // Use a proportion of average spacing, clamped between min and max
  const calculatedSize = avgSpacing * 0.5;
  return Math.min(Math.max(calculatedSize, minSize), maxSize);
}

/**
 * @typedef {Object} TrendDataPoint
 * @property {string} name - Release name (used on x-axis)
 * @property {number} trend - Y-axis value for positioning markers
 */

/**
 * @typedef {Object} EventMarker
 * @property {Object} release
 * @property {string} [release.name] - Release name (optional alternative to release.release_name)
 * @property {string} [release.release_name] - Release name (optional alternative to release.name)
 * @property {string} release.release_date - Date string of the release
 * @property {Object} event - Event object to render
 */

/**
 * Renders custom event markers on a trend chart.
 * 
 * @param {object} props
 * @param {TrendDataPoint[]} props.trendData
 * @param {EventMarker[]} props.eventMarkers
 * @param {Function} props.onEventClick
 * @param {Function} props.onEventExit
 * @param {Object} props.xAxisMap
 * @param {Object} props.yAxisMap
 * @param {Object} props.offset - The offset of the chart container for positioning popups
 * @param {string} [props.trendField='trend'] - The field in trendData to use for Y-axis positioning (overwritten on performance charts)
 * @param {string} [props.chartType='bar'] - The type of chart ('bar' or 'line') to adjust marker positioning logic
 * @returns {JSX.Element}
 * }} props
 */
export const EventMarkers = ({ eventMarkers, trendData, onEventClick, onEventExit, xAxisMap, yAxisMap, offset, trendField='trend', chartType='bar' }) => {
  const xAxis = xAxisMap[Object.keys(xAxisMap)[0]];
  const yAxis = yAxisMap[Object.keys(yAxisMap)[0]];

  const grouped = {};
  eventMarkers.forEach(eventMarker => {
    const key = eventMarker.release.name;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(eventMarker);
  });

  const elements = [];

  Object.entries(grouped).forEach(([releaseName, events]) => {
    const baseX = xAxis.scale ? xAxis.scale(releaseName) : undefined;
    const bandWidth = getIconSizeFromXScale(xAxis.scale, 10, 32);

    if (baseX == null || Number.isNaN(baseX) || bandWidth <= 0) {
      return;
    }

    const cx = (chartType === 'bar' ? baseX + (xAxis.scale.bandwidth() /2)  : baseX); // Center for bar, exact for line

    const thisTrendPos = trendData.find(d => d.name === releaseName);
    const iconSize = Math.min(Math.max(30, bandWidth * 0.7), 32);
    const chartHeight = yAxis.scale.range()[0];

    let baseY = 0;
    if (thisTrendPos) { // Position on trend line
      baseY = yAxis.scale(getNestedValue(thisTrendPos, trendField)) || 0;
    }

    if (baseY == 0) {
      return;
    }

    const isAboveHalf = baseY < (chartHeight / 2) + (chartHeight * 0.1);
    baseY = isAboveHalf ? baseY + (iconSize / 2) : baseY - (iconSize / 2);

    events.forEach((eventData, index) => {
      const { iconPath, severityColourHex } = getEventVisual(eventData.event);

      // Stack based on position relative to chart middle
      let yOffset;
      if (isAboveHalf) {
        // Stack downwards if first marker is above half
        yOffset = index * (iconSize - 4);
      } else {
        // Stack upwards if first marker is below half
        yOffset = -index * (iconSize - 4);
      }

      elements.push(
        <g
          key={`marker-${eventData.event.id}-${releaseName}`}
          transform={`translate(${cx}, ${baseY + yOffset})`}
          style={{ cursor: "pointer" }}
          onMouseEnter={(e) => {
            e.stopPropagation();
            e.preventDefault();
            
            //Calculate popup position based on chart offset and marker position
            const anchorX = (offset?.left ?? 0) + cx;
            const anchorY = (offset?.top  ?? 0) + baseY + yOffset;
            onEventClick(eventData, {
              x: anchorX,
              y: anchorY,
              releaseName
            });
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onEventExit();
          }}
        >
          <circle
            r={iconSize * 0.6}
            fill={severityColourHex}
            stroke="white"
            strokeWidth={2}
            style={{ pointerEvents: "all" }}
          />
          <image
            href={iconPath}
            width={iconSize * 0.6}
            height={iconSize * 0.6}
            x={-(iconSize * 0.3)}   // center horizontally
            y={-(iconSize * 0.3)}   // center vertically
            pointerEvents="none"
            style={{ filter: "drop-shadow(0 0 0.5px white)" }}
          />
        </g>
      );
    });
  });

  return <g style={{ pointerEvents: "auto" }}>{elements}</g>;
};

EventMarkers.propTypes = {
  eventMarkers: PropTypes.array.isRequired,
  trendData: PropTypes.array.isRequired,
  onEventClick: PropTypes.func.isRequired,
  onEventExit: PropTypes.func.isRequired,
  xAxisMap: PropTypes.object.isRequired,
  yAxisMap: PropTypes.object.isRequired,
  offset: PropTypes.object,
  trendField: PropTypes.string,
  chartType: PropTypes.string
};

export const filterTrendData = (projectTrend, daysFilter, selectedMetric) => {

  if (projectTrend?.length < 1) {
    return [];
  } 
  
  const currentDate = new Date();
  const newFilteredData = projectTrend.filter((item) => {
      const itemDate = parseISO(item.release_date);
      return isAfter(itemDate, subDays(currentDate, daysFilter));
    })
    .map((trend) => {
      let passedValue = trend.passed;
      if (selectedMetric === 'technical_debt') {
        passedValue = passedValue / 60; // Convert minutes to hours for technical debt
      }
      return {
        ...trend,
        passed: passedValue,
        trend: passedValue,
        releaseName: ""
      };
    });

  const emaFilteredData = calculateEMA(newFilteredData, 'passed');

  return emaFilteredData;
};